import { lazy, ReactNode, Suspense, useContext, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { RouteContentSkeleton } from '@components/auth-loading-skeleton';
import { CompanySettingsLayout } from '@components/company-settings-layout';
import { EventRouteWrapper } from '@components/event-route-wrapper/event-route-wrapper';
import { generateURL } from '@components/optimized-image/src/utils';
import { ProtectedComponent } from '@components/protected-component';
import { ProtectedImportProcess } from '@components/protected-import-process';
import {
  ENABLED_ADD_VENUE,
  ENABLED_ANALYTICS_ROUTE,
  ENABLED_BOOKING_DETAILS_ROUTE,
  ENABLED_COMPANY_INFO_ROUTE,
  ENABLED_COMPANY_OFFERS_ROUTE,
  ENABLED_COMPANY_WIDGET_ROUTE,
  ENABLED_CUSTOMERS_ROUTE,
  ENABLED_DUPLICATE_EVENT_ROUTE,
  ENABLED_EVENT_CREATE_INFO_ROUTE,
  ENABLED_EVENT_EDIT_DETAILS_ROUTE,
  ENABLED_EVENT_EDIT_INFO_ROUTE,
  ENABLED_EVENT_OFFERS_ROUTE,
  ENABLED_EVENT_PUBLISH_ROUTE,
  ENABLED_INSIGHTS_ROUTE,
  ENABLED_USERS_DETAIL_ROUTE,
  ENABLED_USERS_ROUTE,
} from '@constants/company-auth-permissions';
import {
  ADD_VENUE_ROUTE,
  ANALYTICS_ROUTE,
  BOOKING_DETAILS_HOLDERS_ROUTE,
  BOOKING_DETAILS_ROUTE,
  COMPANY_IMPORT_ROUTE,
  COMPANY_INFO_PATH,
  COMPANY_INTEGRATIONS_PATH,
  COMPANY_ROUTE,
  COMPANY_USERS_PATH,
  COMPANY_WIDGET_PATH,
  CUSTOMERS_ROUTE,
  DUPLICATE_EVENT_INFO_ROUTE,
  EVENT_CREATE_INFO_ROUTE,
  EVENT_EDIT_DETAILS_ROUTE,
  EVENT_EDIT_INFO_ROUTE,
  EVENT_LIST,
  EVENT_PUBLISH_ROUTE,
  EVENT_REPORT_ROUTE,
  INFO_POINT_ROUTE,
  INSIGHTS_ROUTE,
  OFFERS_FLOORPLAN_ROUTE,
  OFFERS_ROUTE,
  PRESET_FLOORPLAN_ROUTE,
  PRESET_LIST_ROUTE,
  PROCESS_IMPORT_ROUTE,
  SELECT_VENUE_ROUTE,
  USER_SETTINGS_ROUTE,
  USERS_DETAIL_ROUTE,
} from '@constants/routes';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { LAYOUT_WITHOUT_SIDEBAR } from '@contexts/layout/layout.type';
import { LayoutContext } from '@contexts/layout/layout-context';
import { Event } from '@domain/event';
import { GET_EVENT_QUERY_KEY, getEventByIdFetch } from '@hooks/events/use-get-event';
import { ReportPageSkeleton } from '@pages/events/[companyId]/[clubSlug]/[eventId]/report-skeleton';

import { queryClient } from './query-client';

const SelectVenue = lazy(() => import('@pages/select-venue'));
const ReportPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/report'));
const InfoPoint = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info-point'));
const CustomersPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/customers/customers'));
const BookingDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/booking/[bookingId]'));
const BookingHolderDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/booking/[code]'));
const EventDuplicateInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/duplicate/info'));
const EventInfoDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info-detail'));
const EventEditInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info'));
const EventEditDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/details'));
const EventCreateInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/info'));
const EventOffers = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/offers'));
const EventOffersFloorplanPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/offers/floorplan'));
const EventPublish = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/publish'));
const EventList = lazy(() => import('@pages/events/[companyId]/[clubSlug]/list'));
const Analytics = lazy(() => import('@pages/events/[companyId]/[clubSlug]/analytics'));
const Insights = lazy(() => import('@pages/events/[companyId]/[clubSlug]/insights'));
const ListPresets = lazy(() => import('@pages/events/[companyId]/[clubSlug]/presets/list'));
const FloorplanPresetPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/presets/floorplan-preset'));
const Users = lazy(() => import('@pages/[companyId]/[clubSlug]/company/users'));
const UserDetail = lazy(() => import('@pages/[companyId]/[clubSlug]/company/users/[userId]'));
const UserSettings = lazy(() => import('@pages/user-settings'));
const CompanyInfo = lazy(() => import('@pages/[companyId]/[clubSlug]/company/info'));
const CompanyIntegrations = lazy(() => import('@pages/[companyId]/[clubSlug]/company/integrations'));
const CompanyWidget = lazy(() => import('@pages/[companyId]/[clubSlug]/company/widget'));
const AddVenue = lazy(() => import('@pages/[companyId]/[clubSlug]/add-venue'));

const EagerLayoutWithoutSidebar = ({ children }: { children: ReactNode }) => {
  const { setLayout } = useContext(LayoutContext);
  useLayoutEffect(() => {
    setLayout({ type: LAYOUT_WITHOUT_SIDEBAR });
  }, [setLayout]);
  return <>{children}</>;
};

const preloadImage = (url: string) => {
  const id = 'lcp-preload';
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href !== url) existing.href = url;
    return;
  }
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  document.head.appendChild(link);
};

const buildCoverImageUrl = (coverUrl: string, aspectRatio?: string) =>
  generateURL({
    src: coverUrl,
    alt: '',
    width: 480,
    format: 'auto',
    quality: 60,
    aspectRatio: aspectRatio === 'square' ? '1:1' : '16:9',
  });

const ReportRouteWrapper = ({ children }: { children: ReactNode }) => {
  const { eventId } = useParams<{ eventId: string }>();

  useLayoutEffect(() => {
    if (eventId) {
      queryClient
        .prefetchQuery({
          queryKey: [GET_EVENT_QUERY_KEY, eventId],
          queryFn: () => getEventByIdFetch(eventId),
          staleTime: 10 * 1000,
        })
        .then(() => {
          const event = queryClient.getQueryData<Event>([GET_EVENT_QUERY_KEY, eventId]);
          if (event?.coverUrl) {
            preloadImage(buildCoverImageUrl(event.coverUrl, event.coverAspectRatio));
          }
        });
    }
  }, [eventId]);

  return <>{children}</>;
};

export const AppRoutes = () => {
  const { isSelectVenueActive, isUserManagementActive, isFloorplanV2Enabled } = useContext(FeatureFlagContext);

  return (
    <Suspense fallback={<RouteContentSkeleton />}>
      <Routes>
        <Route path='/' element={<Navigate replace to={SELECT_VENUE_ROUTE} />} />
        <Route
          path={SELECT_VENUE_ROUTE}
          element={isSelectVenueActive ? <SelectVenue /> : <Navigate replace to={'/'} />}
        />
        {isUserManagementActive && (
          <Route
            path={USERS_DETAIL_ROUTE}
            element={
              <ProtectedComponent permissions={ENABLED_USERS_DETAIL_ROUTE} isRoute>
                <UserDetail />
              </ProtectedComponent>
            }
          />
        )}
        <Route path={USER_SETTINGS_ROUTE} element={<UserSettings />} />
        <Route
          path={PRESET_LIST_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_COMPANY_OFFERS_ROUTE} isRoute>
              <ListPresets />
            </ProtectedComponent>
          }
        />
        {isFloorplanV2Enabled && (
          <Route
            path={PRESET_FLOORPLAN_ROUTE}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_OFFERS_ROUTE} isRoute>
                <FloorplanPresetPage />
              </ProtectedComponent>
            }
          />
        )}
        <Route
          path={EVENT_CREATE_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_CREATE_INFO_ROUTE} isRoute>
              <EventCreateInfo />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_EDIT_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_EDIT_INFO_ROUTE} isRoute>
              <EventRouteWrapper publishedComponent={EventInfoDetails} draftComponent={EventEditInfo} />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_EDIT_DETAILS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_EDIT_DETAILS_ROUTE} isRoute>
              <EventRouteWrapper publishedComponent={EventInfoDetails} draftComponent={EventEditDetails} />
            </ProtectedComponent>
          }
        />
        <Route
          path={INSIGHTS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_INSIGHTS_ROUTE} isRoute>
              <Insights />
            </ProtectedComponent>
          }
        />
        <Route
          path={ANALYTICS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_ANALYTICS_ROUTE} isRoute>
              <Analytics />
            </ProtectedComponent>
          }
        />
        <Route
          path={BOOKING_DETAILS_HOLDERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_BOOKING_DETAILS_ROUTE} isRoute>
              <BookingHolderDetails />
            </ProtectedComponent>
          }
        />
        <Route
          path={BOOKING_DETAILS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_BOOKING_DETAILS_ROUTE} isRoute>
              <BookingDetails />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_REPORT_ROUTE}
          element={
            <EagerLayoutWithoutSidebar>
              <ReportRouteWrapper>
                <Suspense fallback={<ReportPageSkeleton />}>
                  <ReportPage />
                </Suspense>
              </ReportRouteWrapper>
            </EagerLayoutWithoutSidebar>
          }
        />
        <Route
          path={INFO_POINT_ROUTE}
          element={
            <EagerLayoutWithoutSidebar>
              <InfoPoint />
            </EagerLayoutWithoutSidebar>
          }
        />
        <Route path={EVENT_LIST} element={<EventList />} />

        <Route
          path={DUPLICATE_EVENT_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_DUPLICATE_EVENT_ROUTE} isRoute>
              <EventDuplicateInfo />
            </ProtectedComponent>
          }
        />
        {isFloorplanV2Enabled && (
          <Route
            path={OFFERS_FLOORPLAN_ROUTE}
            element={
              <ProtectedComponent permissions={ENABLED_EVENT_OFFERS_ROUTE} isRoute>
                <EventOffersFloorplanPage />
              </ProtectedComponent>
            }
          />
        )}
        <Route
          path={OFFERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_OFFERS_ROUTE} isRoute>
              <EventOffers />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_PUBLISH_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_PUBLISH_ROUTE} isRoute>
              <EventPublish />
            </ProtectedComponent>
          }
        />
        <Route
          path={CUSTOMERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_CUSTOMERS_ROUTE} isRoute>
              <CustomersPage />
            </ProtectedComponent>
          }
        />
        <Route path={COMPANY_ROUTE} element={<CompanySettingsLayout />}>
          <Route
            path={COMPANY_INFO_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                <CompanyInfo />
              </ProtectedComponent>
            }
          />
          <Route
            path={COMPANY_INTEGRATIONS_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                <CompanyIntegrations />
              </ProtectedComponent>
            }
          />
          <Route
            path={COMPANY_WIDGET_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_WIDGET_ROUTE} isRoute>
                <CompanyWidget />
              </ProtectedComponent>
            }
          />
          {isUserManagementActive && (
            <Route
              path={COMPANY_USERS_PATH}
              element={
                <ProtectedComponent permissions={ENABLED_USERS_ROUTE} isRoute>
                  <Users />
                </ProtectedComponent>
              }
            />
          )}
        </Route>
        <Route
          id='addVenue'
          path={ADD_VENUE_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_ADD_VENUE} isRoute>
              <AddVenue />
            </ProtectedComponent>
          }
        />
        <Route id='processImport' path={PROCESS_IMPORT_ROUTE} element={<ProtectedImportProcess />} />
        <Route id='companyImport' path={COMPANY_IMPORT_ROUTE} element={<ProtectedImportProcess />} />
      </Routes>
    </Suspense>
  );
};
